import { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Text, Heading, Input, Select, Button, Spinner, Tag,
  Table, Thead, Tbody, Tr, Th, Td, HStack, InputGroup, InputLeftElement,
  IconButton, Tooltip, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
} from '@chakra-ui/react';
import { Plus, Pencil, Trash2, Send, Search, Bell } from 'lucide-react';
import Pagination from 'react-js-pagination';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../../../components/Header';
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper';
import { PRIMARY_BG } from '../../../theme/brand';

const API_URL = `${process.env.REACT_APP_API_URL}/in-app-messages`;
const ACCENT = PRIMARY_BG;
const LIMIT = 10;

const getHeaders = () => ({
  'x-api-key': localStorage.getItem('api-key'),
  'x-access-token': localStorage.getItem('customer-portal'),
});

const STATUS_META = {
  draft:    { label: 'Draft',    bg: '#F4F4F5', fg: '#71717A', dot: '#A1A1AA' },
  active:   { label: 'Active',   bg: '#DCFCE7', fg: '#166534', dot: '#22C55E' },
  paused:   { label: 'Paused',   bg: '#FFEDD5', fg: '#9A3412', dot: '#F97316' },
  archived: { label: 'Archived', bg: '#E4E4E7', fg: '#52525B', dot: '#71717A' },
};

const TYPE_LABEL = {
  modal: 'Modal',
  banner: 'Banner',
  'slide-in': 'Slide-in',
  fullscreen: 'Fullscreen',
  tooltip: 'Tooltip',
  inbox: 'Inbox',
};

const EVENT_LABEL = {
  app_open: 'App open',
  screen_view: 'Screen view',
  session_start: 'Session start',
  custom_event: 'Custom event',
};

const TABS = ['All', 'Draft', 'Active', 'Paused', 'Archived'];

const fmt = (dateStr) =>
  dateStr ? moment(dateStr).format('DD MMM YYYY') : '-';

function DeleteConfirmModal({ isOpen, onClose, onConfirm, name, isDeleting }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay />
      <ModalContent borderRadius="16px">
        <ModalHeader fontSize="15px">Delete message</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="13px" color="gray.600">
            Are you sure you want to delete <b>{name}</b>? This can't be undone.
          </Text>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="red" isLoading={isDeleting} onClick={onConfirm}>Delete</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

const InAppMessageList = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [messages, setMessages] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 1, limit: LIMIT });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [publishingId, setPublishingId] = useState(null);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', LIMIT);
      if (search) params.set('search', search);
      if (activeTab !== 'All') params.set('status', activeTab.toLowerCase());
      const { data: res } = await axios.get(`${API_URL}?${params.toString()}`, { headers: getHeaders() });
      setMessages(res?.data?.messages || []);
      setPagination(res?.data?.pagination || { total: 0, pages: 1, limit: LIMIT });
    } catch {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, activeTab]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Reset to page 1 whenever the filters change
  useEffect(() => { setPage(1); }, [search, activeTab]);

  const handleEdit = (item) => navigate(`/tools/in-app-designer/${item.id}`);
  const handleCreate = () => navigate('/tools/in-app-designer');

  const handlePublish = async (item) => {
    setPublishingId(item.id);
    try {
      await axios.post(`${API_URL}/${item.id}/publish`, {}, { headers: getHeaders() });
      toast({ title: 'Message published', status: 'success', duration: 2000 });
      loadMessages();
    } catch {
      toast({ title: "Couldn't publish", status: 'error', duration: 3000 });
    } finally {
      setPublishingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/${deleteTarget.id}`, { headers: getHeaders() });
      toast({ title: 'Deleted', status: 'success', duration: 2000 });
      setDeleteTarget(null);
      loadMessages();
    } catch {
      toast({ title: "Couldn't delete", status: 'error', duration: 3000 });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Header />
      <BoxWrapper>
        <Box>
          <Flex py={4} borderBottomWidth="1px" alignItems="center" justifyContent="space-between" mb={5}>
            <Flex align="center" gap={3}>
              <Flex
                w={10} h={10} borderRadius="lg" alignItems="center" justifyContent="center" flexShrink={0}
                style={{ background: `linear-gradient(135deg, ${ACCENT}, var(--brand-primary-hover))` }}
              >
                <Bell color="white" size={20} />
              </Flex>
              <Box>
                <Heading as="h1" size="md" color="gray.900">In-App Notification</Heading>
                <Text fontSize="xs" color="gray.500" mt={0.5}>Manage the in-app modals, banners, and prompts you've built</Text>
              </Box>
            </Flex>
            <Button
              size="sm"
              bg={ACCENT}
              color="white"
              _hover={{ bg: 'var(--brand-primary-active)' }}
              onClick={handleCreate}
              leftIcon={<Plus size={14} />}
              borderRadius="lg"
            >
              Create New
            </Button>
          </Flex>

          <Flex gap={3} mb={4} alignItems="center">
            <InputGroup flex={1} size="sm">
              <InputLeftElement pointerEvents="none">
                <Search size={15} color="#A0AEC0" />
              </InputLeftElement>
              <Input
                placeholder="Search messages by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                borderRadius="lg"
                bg="white"
              />
            </InputGroup>
            <Select
              size="sm"
              w="160px"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              borderRadius="lg"
              bg="white"
            >
              {TABS.map((tab) => <option key={tab} value={tab}>{tab}</option>)}
            </Select>
          </Flex>

          <Box bg="white" borderRadius="xl" borderWidth="1px" overflow="hidden">
            <Table size="sm">
              <Thead bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
                <Tr>
                  <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em">Name</Th>
                  <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em">Type</Th>
                  <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em">Status</Th>
                  <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em">Trigger</Th>
                  <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em">Audience</Th>
                  <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em">Updated</Th>
                  <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em" textAlign="right">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <Tr>
                    <Td colSpan={7} textAlign="center" py={16}>
                      <Flex direction="column" alignItems="center" gap={2}>
                        <Spinner size="sm" color={ACCENT} />
                        <Text fontSize="xs" color="gray.400">Loading messages...</Text>
                      </Flex>
                    </Td>
                  </Tr>
                ) : messages.length === 0 ? (
                  <Tr>
                    <Td colSpan={7} textAlign="center" py={16}>
                      <Flex direction="column" alignItems="center" gap={2}>
                        <Bell color="#CBD5E0" size={32} />
                        <Text fontSize="sm" color="gray.500" fontWeight="medium">No in-app messages yet</Text>
                        <Text fontSize="xs" color="gray.400">Click "Create New" to build your first one</Text>
                      </Flex>
                    </Td>
                  </Tr>
                ) : (
                  messages.map((item, i) => {
                    const statusMeta = STATUS_META[item.status] || STATUS_META.draft;
                    return (
                      <Tr
                        key={item.id}
                        cursor="pointer"
                        _hover={{ bg: 'blue.50' }}
                        onClick={() => handleEdit(item)}
                        borderTopWidth={i === 0 ? 0 : '1px'}
                        transition="background 0.1s"
                      >
                        <Td py={4} maxW="220px">
                          <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>{item.name}</Text>
                        </Td>
                        <Td py={4}>
                          <Text fontSize="sm" fontWeight="500" color="gray.700">{TYPE_LABEL[item.message_type] || item.message_type}</Text>
                        </Td>
                        <Td py={4}>
                          <Tag size="sm" borderRadius="full" display="inline-flex" alignItems="center" gap={1.5} bg={statusMeta.bg} color={statusMeta.fg} px={2.5} py={1} fontSize="xs" fontWeight="600">
                            <Box w={1.5} h={1.5} borderRadius="full" bg={statusMeta.dot} flexShrink={0} />
                            {statusMeta.label}
                          </Tag>
                        </Td>
                        <Td py={4}>
                          <Text fontSize="sm" color="gray.600">{EVENT_LABEL[item.trigger?.event] || item.trigger?.event || '-'}</Text>
                        </Td>
                        <Td py={4}>
                          <Text fontSize="sm" color="gray.600" noOfLines={1} maxW="160px">
                            {item.audience?.type === 'cohort'
                              ? (item.audience?.cohort_name || 'Cohort')
                              : 'All users'}
                          </Text>
                        </Td>
                        <Td py={4}>
                          <Text fontSize="sm" color="gray.600">{fmt(item.updated_at)}</Text>
                        </Td>
                        <Td py={4} onClick={(e) => e.stopPropagation()}>
                          <HStack spacing={1} justify="flex-end">
                            {item.status === 'draft' && (
                              <Tooltip label="Publish" hasArrow fontSize="xs">
                                <IconButton
                                  aria-label="Publish" icon={publishingId === item.id ? <Spinner size="xs" /> : <Send size={13} />}
                                  size="xs" variant="ghost" color="green.600"
                                  isDisabled={publishingId === item.id}
                                  onClick={() => handlePublish(item)}
                                />
                              </Tooltip>
                            )}
                            <Tooltip label="Edit" hasArrow fontSize="xs">
                              <IconButton
                                aria-label="Edit" icon={<Pencil size={13} />}
                                size="xs" variant="ghost" color="gray.500"
                                onClick={() => handleEdit(item)}
                              />
                            </Tooltip>
                            {item.status === 'draft' && (
                              <Tooltip label="Delete" hasArrow fontSize="xs">
                                <IconButton
                                  aria-label="Delete" icon={<Trash2 size={13} />}
                                  size="xs" variant="ghost" color="red.400"
                                  onClick={() => setDeleteTarget(item)}
                                />
                              </Tooltip>
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </Box>

          {pagination.total > LIMIT && (
            <Flex justify="center" mt={5}>
              <Pagination
                activePage={page}
                itemsCountPerPage={pagination.limit || LIMIT}
                totalItemsCount={pagination.total}
                pageRangeDisplayed={5}
                onChange={setPage}
                itemClass="page-item"
                linkClass="page-link"
              />
            </Flex>
          )}
        </Box>

        <DeleteConfirmModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          name={deleteTarget?.name}
          isDeleting={isDeleting}
        />
      </BoxWrapper>
    </>
  );
};

export default InAppMessageList;
